import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Dialog, Field, Input, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  MEMBER_PRICE_LABELS,
  type PatientDetail,
} from '@/features/patients/types';
import { api, isApiError } from '@/lib/api';
import styles from './patients.module.css';

const LAST4 = /^[0-9A-Za-z]{4}$/;

/**
 * The patient's member price status, and (RL-1760, gap #32) "Submit at the
 * counter": the front desk sends a card shown at the counter to Finance's
 * member price review. Only the card type and its last 4 characters are kept.
 */
export function MemberPriceStatus({
  patient,
}: {
  patient: Pick<PatientDetail, 'id' | 'status' | 'memberPriceStatus'>;
}) {
  const { hasPermission } = useSession();
  const [open, setOpen] = useState(false);
  const canSubmit =
    hasPermission('patients:update') &&
    patient.status !== 'merged' &&
    patient.memberPriceStatus !== 'pending';
  return (
    <span className={styles.inlineActions}>
      {patient.memberPriceStatus === 'none' ? (
        '-'
      ) : (
        <Badge
          tone={patient.memberPriceStatus === 'active' ? 'success' : 'warning'}
        >
          {MEMBER_PRICE_LABELS[patient.memberPriceStatus]}
        </Badge>
      )}
      {canSubmit && (
        <Button size="sm" onClick={() => setOpen(true)}>
          Submit a card
        </Button>
      )}
      {open && (
        <CounterCardDialog
          patientId={patient.id}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

function CounterCardDialog({
  patientId,
  onClose,
}: {
  patientId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [cardType, setCardType] = useState('');
  const [last4, setLast4] = useState('');
  const submit = useMutation({
    mutationFn: () =>
      api.post('/finance/member-verifications', {
        patientId,
        cardType: cardType.trim(),
        cardNoLast4: last4.trim(),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['patients'] }),
  });
  const go = () => {
    if (cardType.trim().length < 2 || !LAST4.test(last4.trim()))
      return toast.show({
        tone: 'error',
        title: 'Give the card and its last 4 characters',
      });
    submit
      .mutateAsync()
      .then(() => {
        toast.show({
          tone: 'success',
          title: 'Sent to Finance',
          body: 'Finance reviews the card for the member price.',
        });
        onClose();
      })
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: 'Not sent',
          body: isApiError(err) ? err.message : undefined,
          error: err,
        }),
      );
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Submit a member card"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={submit.isPending} onClick={go}>
            Send to Finance
          </Button>
        </>
      }
    >
      <Field label="Card (organisation)">
        <Input
          value={cardType}
          maxLength={60}
          onChange={(e) => setCardType(e.target.value)}
        />
      </Field>
      <Field
        label="Last 4 characters of the card number"
        hint="Only these are kept."
      >
        <Input
          className="mono"
          value={last4}
          maxLength={4}
          onChange={(e) => setLast4(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}
